import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, TicketPercent } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, euros } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { code: "", kind: "percent", value: 10, active: true, start: "", end: "", max_uses: 0, min_order: 0, used_count: 0 };

export default function PromoAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promo-codes").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editing,
        code: editing.code.trim().toUpperCase(),
        value: Number(editing.value) || 0,
        max_uses: Number(editing.max_uses) || 0,
        min_order: Number(editing.min_order) || 0,
        used_count: Number(editing.used_count) || 0,
      };
      if (editing.id) await api.put(`/admin/promo-codes/${editing.id}`, payload);
      else await api.post("/admin/promo-codes", payload);
      toast.success("Code promo enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce code promo ?")) return;
    await api.delete(`/admin/promo-codes/${id}`);
    toast.success("Supprimé");
    load();
  };

  const toggle = async (item) => {
    await api.put(`/admin/promo-codes/${item.id}`, { ...item, active: !item.active });
    load();
  };

  return (
    <div data-testid="admin-promos">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Codes promotionnels</h1>
          <p className="text-sm text-muted-foreground mt-1">Prêts pour l'activation du paiement en ligne (phase 2).</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="promo-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Créer un code
        </button>
      </div>

      {!items ? <p className="text-muted-foreground">Chargement…</p> : items.length === 0 ? (
        <div className="bg-white border border-line p-10 text-center" data-testid="promos-empty">
          <TicketPercent className="w-8 h-8 text-rouge mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun code promo. Créez le premier.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="promos-list">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`promo-row-${p.id}`}>
              <TicketPercent className="w-5 h-5 text-rouge shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="font-mono font-bold text-onyx">{p.code} {!p.active && <span className="text-xs font-sans text-muted-foreground">(désactivé)</span>}</p>
                <p className="text-sm text-muted-foreground">
                  {p.kind === "percent" ? `-${p.value}%` : `-${euros(p.value)}`}
                  {p.min_order > 0 && ` · min. ${euros(p.min_order)}`}
                  {p.max_uses > 0 && ` · ${p.used_count}/${p.max_uses} utilisations`}
                  {(p.start || p.end) && ` · ${p.start || "…"} → ${p.end || "…"}`}
                </p>
              </div>
              <Switch checked={!!p.active} onCheckedChange={() => toggle(p)} data-testid={`promo-active-${p.id}`} />
              <button onClick={() => setEditing({ ...p })} data-testid={`promo-edit-${p.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} data-testid={`promo-delete-${p.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="promo-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Créer"} un code promo</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Code *</Label><Input data-testid="promo-code-input" className="mt-1.5 font-mono uppercase" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} placeholder="LANCEMENT10" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de réduction</Label>
                  <select data-testid="promo-kind-select" className="mt-1.5 w-full border border-input bg-white px-3 py-2 text-sm"
                    value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                    <option value="percent">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe (€)</option>
                  </select>
                </div>
                <div>
                  <Label>Valeur {editing.kind === "percent" ? "(%)" : "(€)"}</Label>
                  <Input type="number" step="0.01" min="0" data-testid="promo-value-input" className="mt-1.5" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} />
                </div>
                <div><Label>Date de début</Label><Input type="date" data-testid="promo-start-input" className="mt-1.5" value={editing.start} onChange={(e) => setEditing({ ...editing, start: e.target.value })} /></div>
                <div><Label>Date de fin</Label><Input type="date" data-testid="promo-end-input" className="mt-1.5" value={editing.end} onChange={(e) => setEditing({ ...editing, end: e.target.value })} /></div>
                <div><Label>Utilisations max (0 = illimité)</Label><Input type="number" min="0" data-testid="promo-max-uses-input" className="mt-1.5" value={editing.max_uses} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} /></div>
                <div><Label>Commande minimum (€)</Label><Input type="number" step="0.01" min="0" data-testid="promo-min-order-input" className="mt-1.5" value={editing.min_order} onChange={(e) => setEditing({ ...editing, min_order: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="promo-active-switch" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Actif</Label>
              </div>
              <button onClick={save} disabled={saving || !editing.code} data-testid="promo-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
